import React, { useMemo } from 'react';
import { Box, useIsCardMailWithin7Days, useResponsive } from '@common-fe/common-fe';
import { FiservCardInterface } from '@common-fe/common-fe';

import { CloseActionCardType } from '@/modules/transaction/components/Cards/Cards.types.ts';
import { useIsLockCardPurseAvailable } from '@/modules/transaction/components/Cards/hooks/useIsLockCardPurseAvailable';
import { useShouldShowCardActions } from '@/modules/transaction/components/Cards/hooks/useShouldShowCardActions.ts';

import CardAccountsAccessCard from './CardStatusActionWrappers/CardAccountsAccessCard';
import CardCloseActionCard from './CardStatusActionWrappers/CardCloseActionCard';
import CardLockUnlockActionCard from './CardStatusActionWrappers/CardLockUnlockActionCard';
import CardReplaceActionCard from './CardStatusActionWrappers/CardReplaceActionCard';

interface CardActionButtonsProps {
  card: FiservCardInterface;
  loadingButton: boolean;
  onOpenLockModal: () => void;
  onOpenLockUnlockCardModal: () => void;
  onOpenCardReplacementModal: () => void;
  onOpenCloseConfirmationModal: () => void;
  onOpenManageAccountAccessModal: () => void;
}

const CardActionButtons: React.FC<CardActionButtonsProps> = ({
  card,
  loadingButton,
  onOpenLockModal,
  onOpenLockUnlockCardModal,
  onOpenCardReplacementModal,
  onOpenCloseConfirmationModal,
  onOpenManageAccountAccessModal,
}) => {
  const { isMobile } = useResponsive();
  const isMailWithin7Days = useIsCardMailWithin7Days(card);
  const isLockCardPurseAvailable = useIsLockCardPurseAvailable();
  const { showLockUnlockActionCard, showCloseActionCard, showReplaceActionCard } =
    useShouldShowCardActions(card);
  const showAccountsAccessCard = isLockCardPurseAvailable && showLockUnlockActionCard;

  const typeOfCloseActionCard = useMemo(() => {
    if (showLockUnlockActionCard && showReplaceActionCard) {
      return CloseActionCardType.SECONDARY;
    } else {
      return CloseActionCardType.PRIMARY;
    }
  }, [showLockUnlockActionCard, showReplaceActionCard]);

  const isStandaloneCloseActionCard = showCloseActionCard && !(showLockUnlockActionCard && showReplaceActionCard);
  const actionCardsCount =
    Number(showLockUnlockActionCard) + Number(showReplaceActionCard) + Number(isStandaloneCloseActionCard);

  const renderActionCards = (
    <Box
      direction={isMobile ? 'column' : 'row'}
      gap={isMobile ? 'spacing12' : 'spacing24'}
      width={isMobile ? '100%' : undefined}
      justify={actionCardsCount === 1 ? 'center' : undefined}
    >
      {showLockUnlockActionCard && (
        <CardLockUnlockActionCard
          card={card}
          loadingButton={loadingButton}
          onOpenLockModal={onOpenLockModal}
          onOpenLockUnlockCardModal={onOpenLockUnlockCardModal}
        />
      )}
      {showReplaceActionCard && (
        <CardReplaceActionCard
          isMailWithin7Days={isMailWithin7Days}
          loadingButton={loadingButton}
          onOpenCardReplacementModal={onOpenCardReplacementModal}
          card={card}
        />
      )}
      {isStandaloneCloseActionCard && (
        <CardCloseActionCard
          displayType={typeOfCloseActionCard}
          loadingButton={loadingButton}
          onOpenCloseConfirmationModal={onOpenCloseConfirmationModal}
        />
      )}
    </Box>
  );

  return (
    <Box
      direction="column"
      gap={isMobile ? 'spacing12' : 'spacing24'}
      width={isMobile ? '100%' : undefined}
      pad={isMobile ? undefined : { horizontal: 'spacing24' }}
    >
      {renderActionCards}
      {showAccountsAccessCard && (
        <CardAccountsAccessCard
          loadingButton={loadingButton}
          onOpenManageAccountAccessModal={onOpenManageAccountAccessModal}
        />
      )}
      {showCloseActionCard && showLockUnlockActionCard && showReplaceActionCard && (
        <CardCloseActionCard
          displayType={typeOfCloseActionCard}
          loadingButton={loadingButton}
          onOpenCloseConfirmationModal={onOpenCloseConfirmationModal}
        />
      )}
    </Box>
  );
};

export default React.memo(CardActionButtons);
